'use client'

import { memo, useCallback, useMemo } from 'react'
import clsx from 'clsx'
import { isNil } from 'ramda'

import {
  checkIfCanSelectSlot,
  TMSDeliverySlotsArraySlot
} from 'modules/delivery'
import Checkbox from 'shared/ui/form/Checkbox'

import styles from './DeliverySlot.module.scss'

export type DeliverySlotProps = {
  time: string
  value: boolean
  onChange: (name: string, value: boolean) => void
  slot?: TMSDeliverySlotsArraySlot
  selectedSlots: TMSDeliverySlotsArraySlot[]
  hoverPreviewSlots: {
    slots: TMSDeliverySlotsArraySlot[]
    action: 'add' | 'remove'
  } | null
  setHoverPreviewSlots: React.Dispatch<
    React.SetStateAction<{
      slots: TMSDeliverySlotsArraySlot[]
      action: 'add' | 'remove'
    } | null>
  >
  shownAffectedSlots: (
    slot: TMSDeliverySlotsArraySlot,
    action: 'add' | 'remove'
  ) => TMSDeliverySlotsArraySlot[]
}

export const DeliverySlot = memo(
  ({
    time,
    onChange,
    value,
    slot,
    selectedSlots,
    hoverPreviewSlots,
    setHoverPreviewSlots,
    shownAffectedSlots
  }: DeliverySlotProps): React.ReactNode => {
    const isDisabled = isNil(slot)
    const canSelectSlot = useMemo(
      () => checkIfCanSelectSlot(selectedSlots, slot),
      [selectedSlots, slot]
    )

    const handleMouseEnter = useCallback(() => {
      if (isDisabled || !slot) return

      const action = value ? 'remove' : 'add'

      if (action === 'add' && !canSelectSlot) return

      const affectedSlots = shownAffectedSlots(slot, action)

      setHoverPreviewSlots({ slots: affectedSlots, action })
    }, [
      isDisabled,
      slot,
      value,
      canSelectSlot,
      shownAffectedSlots,
      setHoverPreviewSlots
    ])

    const handleMouseLeave = useCallback(() => {
      setHoverPreviewSlots(null)
    }, [setHoverPreviewSlots])

    const isPreview = useMemo(
      () => hoverPreviewSlots?.slots.some(s => s.time === time),
      [hoverPreviewSlots, time]
    )

    const previewAction = isPreview ? hoverPreviewSlots?.action : undefined

    return (
      <li
        className={clsx(styles.slot, {
          [styles.active]: value,
          [styles.previewAdd]: previewAction === 'add',
          [styles.previewRemove]: previewAction === 'remove',
          [styles.disabled]: isDisabled,
          [styles.freeForCart]: slot?.isFreeForCart
        })}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <Checkbox
          noMargin
          name='delivery-slot'
          value={value}
          onChange={onChange}
          label={time}
          disabled={!canSelectSlot || isDisabled}
          aria-label={`${time} ${slot?.date || ''}`}
        />
      </li>
    )
  }
)

DeliverySlot.displayName = 'DeliverySlot'
